import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button, Card, Notification, Typography } from '@kustom-checkout/morpheus';

import { createKssSession, postSelectedOption } from '~/api';
import type { AppConfig } from '~/api/types';
import { AddressConfig } from '~/components/AddressConfig';
import { AggregatorConfig } from '~/components/AggregatorConfig';
import { DebugPanel } from '~/components/DebugPanel';
import { Header } from '~/components/Header';
import { OtherConfig } from '~/components/OtherConfig';
import { ShippingModulePanel } from '~/components/ShippingModulePanel';
import { TestRunner } from '~/components/TestRunner';
import {
  readConfigurationFromQuery,
  writeConfigurationToQuery,
  type ShipwreckConfiguration,
} from '~/lib/queryState';

type ErrorKind =
  | 'load-visual-test'
  | 'post-selected-option'
  | 'run-test'
  | 'unauthorized';

const ERROR_MESSAGES: Record<ErrorKind, string> = {
  'load-visual-test': 'The "Load visual test" operation seems to have failed.',
  'post-selected-option':
    'The "select shipping option" operation seems to have failed.',
  'run-test': 'The "run integration test" operation seems to have failed.',
  unauthorized:
    'There was an authorization issue in your request. Please contact the merchant shipping team (kco.e@kustom.com) to look at it.',
};

interface Session {
  clientToken: string;
  clientLocale: string;
  kssSessionId: string;
  kssVersion: string;
}

export function App({ config }: { config: AppConfig }) {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [configuration, setConfiguration] = useState<ShipwreckConfiguration>(
    () => readConfigurationFromQuery(window.location.search),
  );
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<ErrorKind | null>(null);
  const [showTest, setShowTest] = useState(false);
  const [isPostingOption, setIsPostingOption] = useState(false);
  const [recaptchaAnswer, setRecaptchaAnswer] = useState<string | null>(null);

  useEffect(() => {
    writeConfigurationToQuery(configuration);
  }, [configuration]);

  const verifyRecaptcha = useCallback(async () => {
    if (!config.recaptcha_enabled || !executeRecaptcha) return null;

    const token = await executeRecaptcha('homepage');
    setRecaptchaAnswer(token);
    return token;
  }, [config.recaptcha_enabled, executeRecaptcha]);

  const loadClient = async () => {
    const answer = await verifyRecaptcha();

    try {
      const { data } = await createKssSession({
        ...configuration,
        recaptcha_answer: answer,
      });

      setSession({
        clientToken: data.client_token,
        clientLocale: data.locale,
        kssSessionId: data.kss_session_id,
        kssVersion: data.service_version,
      });
      setShowTest(false);
      setError(null);
    } catch (caught) {
      const status = (caught as { response?: { status?: number } }).response
        ?.status;
      setSession(null);
      setIsPostingOption(false);
      setError(status === 401 ? 'unauthorized' : 'load-visual-test');
    }
  };

  const submitSelectedOption = async () => {
    if (!session) return;

    setError(null);
    setIsPostingOption(true);

    try {
      await postSelectedOption({
        aggregator: configuration.aggregator,
        recaptcha_answer: recaptchaAnswer,
        clientToken: session.clientToken,
      });
    } catch {
      setError('post-selected-option');
    } finally {
      setIsPostingOption(false);
    }
  };

  const startTest = async () => {
    await verifyRecaptcha();
    setError(null);
    setShowTest(true);
  };

  const onTestComplete = useCallback(
    (kssSessionId: string, kssVersion: string) => {
      setSession((current) =>
        current ? { ...current, kssSessionId, kssVersion } : current,
      );
    },
    [],
  );

  const onTestFailed = useCallback((status?: number) => {
    setShowTest(false);
    setIsPostingOption(false);
    setError(status === 401 ? 'unauthorized' : 'run-test');
  }, []);

  return (
    <div className="bg-surface text-on-surface min-h-screen">
      <Header />
      <main className="flex flex-row items-start justify-start gap-8 p-4">
        {/* Sections hold their heading close and sit further from each other,
            so a heading reads as belonging to the fields below it. */}
        <Card className="flex w-[380px] max-w-[50%] flex-col gap-7 p-4 shadow-lg">
          <section className="flex flex-col gap-2">
            <Typography variant="h3">TMS configuration</Typography>
            <AggregatorConfig
              name="aggregator-config"
              aggregator={configuration.aggregator}
              onChange={(aggregator) =>
                setConfiguration((current) => ({
                  ...current,
                  aggregator,
                }))
              }
            />
          </section>

          <section className="flex flex-col gap-2">
            <Typography variant="h3">Address</Typography>
            <AddressConfig
              name="shipment-config"
              shippingAddress={configuration.shipping_address}
              onChange={(shipping_address) =>
                setConfiguration((current) => ({ ...current, shipping_address }))
              }
            />
          </section>

          <section className="flex flex-col gap-2">
            <Typography variant="h3">Other</Typography>
            <OtherConfig
              name="other-config"
              locale={configuration.locale}
              currency={configuration.currency}
              onChange={(values) =>
                setConfiguration((current) => ({ ...current, ...values }))
              }
            />
          </section>

          <div className="flex w-full flex-col gap-2">
            <Button id="load-visual-test" onClick={loadClient}>
              Load visual test
            </Button>
            <Button variant="secondary" onClick={startTest} disabled={showTest}>
              Run integration test
            </Button>
            {session && !showTest && (
              <Button
                variant="secondary"
                onClick={submitSelectedOption}
                disabled={isPostingOption}
              >
                Post selected shipping option
              </Button>
            )}
          </div>

          {error && (
            <Notification
              variant="error"
              fullWidth
              message={
                <>
                  <strong>Sorry!</strong> {ERROR_MESSAGES[error]}
                </>
              }
            />
          )}

          <DebugPanel
            kssSessionId={session?.kssSessionId}
            serviceVersion={session?.kssVersion}
          />
        </Card>

        <div className="flex min-w-0 flex-1 flex-col items-start pl-8">
          <div className="w-full max-w-[500px]">
            {showTest ? (
              <TestRunner
                configuration={configuration}
                recaptchaAnswer={recaptchaAnswer}
                onComplete={onTestComplete}
                onError={onTestFailed}
              />
            ) : (
              <ShippingModulePanel
                clientToken={session?.clientToken}
                locale={session?.clientLocale}
                orderId={session?.kssSessionId}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
